/**
 * Per-file status, with the review state filled in.
 *
 * The dataset file list (`GET /datasets/{id}/files`) is the complete set of a
 * dataset's files, but it does not reliably know a file's review state: a
 * document parked for field review still reads `uploaded` there (seen on dev,
 * 2026-10-06), which the UI shows as "Queued" or a processing hourglass. The
 * pipeline jobs (`GET /datasets/{id}/jobs`) do carry `awaiting_review` per
 * file. This overlays the job state onto the list where it is more specific.
 *
 * The list decides WHICH files exist — the card counts are derived from it —
 * so a job row never adds a file the list does not carry. When the list is
 * empty the job rows are the list, as a fallback.
 *
 * Only `awaiting_review` is overlaid. /jobs returns EVERY job for the dataset,
 * not just the latest, and a failed first run stamps all of its files `failed`
 * for good — so letting any job status win would turn a since-reprocessed,
 * `complete` file red (or bring back the hourglass from a stuck old job). The
 * list is current for everything it can express; review state is the one
 * thing it cannot, so that is the one thing taken from the jobs.
 *
 * Lower number = worse / more specific; unlisted statuses rank last, so a
 * stale `uploaded` row loses to `awaiting_review`, while `failed` does not.
 */
const STATUS_PRIORITY = {
  // PRD-1628: `rejected` was refused at upload and has no job to overrule it.
  rejected: 0, failed: 0, processing: 1, queued: 2, awaiting_input: 3, awaiting_review: 3, success: 4, complete: 4,
};

const rank = (status) => STATUS_PRIORITY[status] ?? 99;

/** Flattens either /jobs shape — entries with a `files` array, or flat rows. */
export const flattenJobFiles = (jobs) => {
  const rows = [];
  for (const entry of jobs || []) {
    if (Array.isArray(entry?.files)) rows.push(...entry.files);
    else if (entry?.file_id || entry?.file_name) rows.push(entry);
  }
  return rows;
};

/**
 * @param {Array<object>} listFiles rows from /files (`id` or `file_id`)
 * @param {Array<object>} jobs rows from /jobs
 * @returns {Array<object>} the list rows (same objects, status possibly
 *   replaced), or the deduplicated job rows when the list is empty
 */
export const mergeFileStatuses = (listFiles, jobs) => {
  const jobRows = flattenJobFiles(jobs);
  const keyOf = (f) => f.file_id || f.id || f.file_name;

  if (!listFiles || listFiles.length === 0) {
    const byKey = new Map();
    jobRows.forEach((row, i) => {
      const key = keyOf(row) || `_anon_${i}`;
      const existing = byKey.get(key);
      if (!existing || rank(row.status) < rank(existing.status)) byKey.set(key, row);
    });
    return Array.from(byKey.values());
  }

  const parked = new Set();
  for (const row of jobRows) {
    const key = keyOf(row);
    if (key && row.status === 'awaiting_review') parked.add(key);
  }

  return listFiles.map((file) => (
    parked.has(keyOf(file)) && rank('awaiting_review') < rank(file.status)
      ? { ...file, status: 'awaiting_review' }
      : file
  ));
};
